import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ClaimDetailsModal.css';
import SoundManager from '../../services/SoundManager';

interface ClaimDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimData: {
    baseAmount: number;
    multiplier: number;
    effects: string[];
    bonuses: {
      vip: string;
      vipName?: string;
      vipAmount?: number;
      rank: string;
      rankName?: string;
      rankAmount?: number;
      leader: string;
      leaderName?: string;
      leaderAmount?: number;
      boost: string;
      boostAmount?: number;
    };
    claimedAmount: number;
  };
}

const ClaimDetailsModal: React.FC<ClaimDetailsModalProps> = ({ isOpen, onClose, claimData }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      SoundManager.playModalOpen();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const { baseAmount, multiplier, effects, bonuses, claimedAmount } = claimData;

  // Используем абсолютные значения в SPERM (честное сравнение)
  const vipAmount = bonuses.vipAmount || 0;
  const rankAmount = bonuses.rankAmount || 0;
  const leaderAmount = bonuses.leaderAmount || 0;
  const boostAmount = bonuses.boostAmount || 0;

  // Общая сумма бонусов
  const totalBonusAmount = vipAmount + rankAmount + leaderAmount + boostAmount;

  // Определяем какой случайный множитель сработал
  const getRandomMultiplierText = () => {
    if (multiplier >= 5) return `🎰 ${t('modals.claimDetailsModal.step2MegaHarvest')}`;
    if (multiplier >= 3) return `🌟 ${t('modals.claimDetailsModal.step2SuperHarvest')}`;
    if (multiplier >= 2) return `🌾 ${t('modals.claimDetailsModal.step2GoldenHarvest')}`;
    return `🪙 ${t('modals.claimDetailsModal.step2Normal')}`;
  };

  // Проверяем какие эффекты сработали
  const hasCrit = effects.includes('critical_strike');
  const hasCombo = effects.includes('combo_active');
  const hasMinigameBoost = effects.includes('minigame_boost');

  // Расчет пошаговый
  const baseMultiplied = baseAmount * (multiplier >= 2 ? multiplier : 1);

  return (
    <div className="claim-details-overlay" onClick={onClose}>
      <div className="claim-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="claim-details-header">
          <h2>📊 {t('modals.claimDetailsModal.title')}</h2>
          <button className="claim-details-close" onClick={onClose}>✕</button>
        </div>

        <div className="claim-details-body">
          {/* Итоговая сумма */}
          <div className="claim-total-section">
            <div className="claim-total-label">{t('modals.claimDetailsModal.received')}</div>
            <div className="claim-total-amount">+{claimedAmount.toFixed(2)} SPERM</div>
          </div>

          {/* Расчет по шагам */}
          <div className="claim-calculation-steps">
            {/* Шаг 1: Базовая сумма */}
            <div className="calc-step">
              <div className="calc-step-icon">1️⃣</div>
              <div className="calc-step-content">
                <div className="calc-step-title">{t('modals.claimDetailsModal.step1')}</div>
                <div className="calc-step-value">{baseAmount} SPERM</div>
              </div>
            </div>

            {/* Шаг 2: Случайный множитель */}
            {multiplier >= 2 && (
              <div className="calc-step highlight">
                <div className="calc-step-icon">2️⃣</div>
                <div className="calc-step-content">
                  <div className="calc-step-title">{getRandomMultiplierText()}</div>
                  <div className="calc-step-formula">
                    {baseAmount} × {multiplier >= 2 ? Math.floor(multiplier) : 1} = {baseMultiplied.toFixed(2)} SPERM
                  </div>
                </div>
              </div>
            )}

            {/* Шаг 3: Критический удар и комбо */}
            {(hasCrit || hasCombo) && (
              <div className="calc-step highlight">
                <div className="calc-step-icon">3️⃣</div>
                <div className="calc-step-content">
                  <div className="calc-step-title">{t('modals.claimDetailsModal.systemMultipliers')}</div>
                  {hasCrit && (
                    <div className="calc-effect">{t('modals.claimDetailsModal.criticalStrike')}</div>
                  )}
                  {hasCombo && (
                    <div className="calc-effect">{t('modals.claimDetailsModal.comboActive')}</div>
                  )}
                  <div className="calc-step-formula">
                    {t('modals.claimDetailsModal.totalMultiplier')} x{multiplier.toFixed(4)}
                  </div>
                </div>
              </div>
            )}

            {/* Шаг 4: Бонусы (только 4 типа: VIP, Ранг, Лидер, Мини-игры) - показываем SPERM */}
            {(vipAmount > 0 || rankAmount > 0 || leaderAmount > 0 || boostAmount > 0) && (
              <div className="calc-step">
                <div className="calc-step-icon">4️⃣</div>
                <div className="calc-step-content">
                  <div className="calc-step-title">{t('modals.claimDetailsModal.bonuses')}</div>
                  <div className="calc-bonuses">
                    {vipAmount > 0 && (
                      <div className="calc-bonus-item">
                        💎 {t('modals.claimDetailsModal.vipBonus')} {bonuses.vipName ? `(${bonuses.vipName})` : ''}: <span className="bonus-sperm">+{vipAmount.toFixed(4)} SPERM</span>
                      </div>
                    )}
                    {rankAmount > 0 && (
                      <div className="calc-bonus-item">
                        🎖️ {t('modals.claimDetailsModal.rankBonus')} {bonuses.rankName ? `(${bonuses.rankName})` : ''}: <span className="bonus-sperm">+{rankAmount.toFixed(4)} SPERM</span>
                      </div>
                    )}
                    {leaderAmount > 0 && (
                      <div className="calc-bonus-item">
                        👑 {t('modals.claimDetailsModal.leaderBonus')} {bonuses.leaderName ? `(${bonuses.leaderName})` : ''}: <span className="bonus-sperm">+{leaderAmount.toFixed(4)} SPERM</span>
                      </div>
                    )}
                    {boostAmount > 0 && (
                      <div className="calc-bonus-item">
                        🎮 {t('modals.claimDetailsModal.gameBoost')}: <span className="bonus-sperm">+{boostAmount.toFixed(4)} SPERM</span>
                      </div>
                    )}
                  </div>
                  <div className="calc-bonus-total">
                    {t('modals.claimDetailsModal.totalBonuses')}: <span className="bonus-sperm-total">+{totalBonusAmount.toFixed(4)} SPERM</span>
                  </div>
                </div>
              </div>
            )}

            {/* Итоговая формула */}
            <div className="calc-step final">
              <div className="calc-step-icon">✅</div>
              <div className="calc-step-content">
                <div className="calc-step-title">{t('modals.claimDetailsModal.finalCalculation')}</div>
                <div className="calc-step-formula">
                  {baseAmount.toFixed(2)} + {totalBonusAmount.toFixed(4)} = <strong>{claimedAmount.toFixed(2)} SPERM</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Эффекты которые сработали */}
          {effects.length > 0 && (
            <div className="claim-effects-section">
              <h3>{t('modals.claimDetailsModal.activeEffects')}</h3>
              <div className="claim-effects-list">
                {effects.map((effect, index) => (
                  <div key={index} className="effect-badge">
                    {effect === 'critical_strike' && t('modals.claimDetailsModal.effectCrit')}
                    {effect === 'combo_active' && t('modals.claimDetailsModal.effectCombo')}
                    {effect === 'minigame_boost' && t('modals.claimDetailsModal.effectBoost')}
                    {effect === 'golden_harvest' && t('modals.claimDetailsModal.effectGoldenHarvest')}
                    {effect === 'super_harvest' && t('modals.claimDetailsModal.effectSuperHarvest')}
                    {effect === 'mega_harvest' && t('modals.claimDetailsModal.effectMegaHarvest')}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="claim-details-footer">
          <button className="claim-details-btn" onClick={onClose}>
            {t('modals.claimDetailsModal.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClaimDetailsModal;
